import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useLazyQuery, gql } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  TablePagination,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BulkSimUpload from "./BulkSimUpload";
import DeleteSimDialog from "./DeleteSimDialog";
import EditSimDialog from "./EditSimDialog";
import AddNewSim from "./AddNewSim";
import { ReactComponent as SampleFileIcon } from "../../assets/svg/sample_file.svg";
import { ReactComponent as AddDeviceIcon } from '../../assets/svg/sim_white.svg';
import { ReactComponent as BulkUploadIcon } from '../../assets/svg/bulk_upload.svg';
import { useAuth } from "../../hooks/useAuth";
const GET_ALL_SIMS = gql`
  query GetAllSims($pageSize: String!, $page: String!) {
    getAllSims(pageSize: $pageSize, page: $page) {
      totalCount
      data {
        sim_id
        iccid
        activation_status
        sim_status
        sim_subscription_start_date
        sim_subscription_end_date
        created_on
        tcu_id
        sim_provider
        tsp_1
        tsp_2
        msisdn_1
        msisdn_2
        serial_number
        imei
        tcu_make
        tcu_model
        created_by
        username
        whitelist_status
        whitelist_ticket_no
        whitelist_ticket_status
        whitelist_ticket_remark
        whitelist_raised_at
        whitelist_hosts
        whitelist_msisdns
      }
    }
  }
`;
const SEARCH_SIM = gql`
  query SearchSim($searchType: SimSearchType!, $searchItem: String!) {
    searchSim(searchType: $searchType, searchItem: $searchItem) {
      sim_id
      iccid
      activation_status
      sim_status
      sim_subscription_start_date
      sim_subscription_end_date
      created_on
      tcu_id
      sim_provider
      tsp_1
      tsp_2
      msisdn_1
      msisdn_2
      serial_number
      imei
      tcu_make
      tcu_model
      created_by
      username
      whitelist_status
      whitelist_ticket_no
      whitelist_ticket_status
      whitelist_ticket_remark
      whitelist_raised_at
      whitelist_hosts
      whitelist_msisdns
    }
  }
`;
const DOWNLOAD_SIM_INVENTORY = gql`
  query DownloadSimInventory {
    downloadSimInventory {
      url
    }
  }
`;
// Whitelist status as the page shows it.
//
// RAISED only means Sensorise accepted the ticket; whether it is done is the
// ticket's own status ("Pending" until they process it), so that is what is
// shown for a raised SIM. FAILED means the upload was refused and the next
// daily run sends it again.
type WhitelistView = { label: string; color: string; bg: string; border: string; tooltip: string } | null;
const whitelistView = (row: any): WhitelistView => {
  if (!row.whitelist_status) return null;
  const raisedAt = row.whitelist_raised_at
    ? new Date(row.whitelist_raised_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
    : '';
  if (row.whitelist_status === 'FAILED') {
    return {
      label: 'Failed',
      color: '#d32f2f', bg: '#fff0f3', border: '#f48fb1',
      tooltip: `Sensorise refused the upload${raisedAt ? ` on ${raisedAt}` : ''}; the next daily run resends it.`,
    };
  }
  const ticketStatus = row.whitelist_ticket_status || 'Raised';
  const done = /^(closed|resolved|completed|done)$/i.test(ticketStatus);
  return {
    label: ticketStatus,
    color: done ? '#2e7d32' : '#ed6c02',
    bg: done ? '#edf7ed' : '#fff4e5',
    border: done ? '#a5d6a7' : '#ffcc80',
    tooltip: [
      `Ticket ${row.whitelist_ticket_no || '-'}`,
      raisedAt && `raised ${raisedAt}`,
      row.whitelist_ticket_remark,
    ].filter(Boolean).join(' · '),
  };
};
const cellStyle = {
  fontSize: '14px',
  padding: '8px',
  width: 'auto',
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
  color: '#000',
  whiteSpace: 'nowrap',
};
const SimManagement: React.FC = () => {
  const auth = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSim, setSelectedSim] = useState<any>(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [addSimOpen, setAddSimOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("imei");
  const [totalCount, setTotalCount] = useState(0);
  // Queries
  const { loading: allSimsLoading, error, data, refetch } = useQuery(GET_ALL_SIMS, {
    variables: {
      pageSize: rowsPerPage.toString(),
      page: (page + 1).toString()
    },
    skip: searchTerm.trim().length > 0,
    fetchPolicy: 'cache-and-network'
  });
  const [searchSim, { data: searchData, loading: searchLoading }] = useLazyQuery(SEARCH_SIM);
  const [downloadInventory, { loading: downloadLoading }] = useLazyQuery(DOWNLOAD_SIM_INVENTORY);
  // State for table data
  const [simData, setSimData] = useState<any[]>([]);
  // Updated mapSimRows function
  const mapSimRows = useCallback((rows: any[]) => {
    return rows.map((row) => ({
      sim_id: row.sim_id,
      manuf: row.tcu_make || '',
      modelCode: '', // Not available in query
      model: row.tcu_model || '',
      imei: row.imei || '',
      serial: row.serial_number || '',
      simProvider: row.sim_provider,
      iccid: row.iccid || '',
      activationType: row.activation_status || '',
      activatedOnTsp1: row.tsp_1 ? `${row.tsp_1}, ${row.sim_subscription_start_date ? formateDateOnly(row.sim_subscription_start_date) : ''}` : '',
      activatedOnTsp2: row.tsp_2 ? `${row.tsp_2}, ${row.sim_subscription_start_date ? formateDateOnly(row.sim_subscription_start_date) : ''}` : '',
      validUptoTsp1: row.tsp_1 ? `${row.tsp_1}, ${row.sim_subscription_end_date ? formateDateOnly(row.sim_subscription_end_date) : ''}` : '',
      validUptoTsp2: row.tsp_2 ? `${row.tsp_2}, ${row.sim_subscription_end_date ? formateDateOnly(row.sim_subscription_end_date) : ''}` : '',
      msisdn1: row.msisdn_1 || '',
      msisdn2: row.msisdn_2 || '',
      // From the backend's sim_whitelist ledger: what was sent to Sensorise for
      // this SIM. Null on a SIM that has never been whitelisted.
      whiteListedIp: row.whitelist_hosts || '',
      whiteListedPhone: row.whitelist_msisdns || '',
      whitelist: whitelistView(row),
      simStatus: row.sim_status || '',
      createdBy: row.username || row.created_by || '',
      createdOn: row.created_on ? formatDate(row.created_on) : '',
      tcu_id: row.tcu_id,
      tsp_1: row.tsp_1,
      tsp_2: row.tsp_2,
    }));
  }, []);
  // Helper to format unix timestamp
  const formatDate = (epochTime: number): string => {
    const date = new Date(epochTime * 1000);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };
  const formateDateOnly =(epoch: number): string => 
    formatDate(epoch).split(' ')[0];
  // Handle data updates
  useEffect(() => {
    if (data?.getAllSims) {
      setSimData(mapSimRows(data.getAllSims.data));
      setTotalCount(data.getAllSims.totalCount);
    }
  }, [data, mapSimRows]);
  // Handle search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      let gqlSearchType = searchType;
      if (searchType === 'serial') gqlSearchType = 'serial_number';
      if (searchTerm.trim().length > 0) {
        setPage(0);
        searchSim({ variables: { searchType: gqlSearchType, searchItem: searchTerm.trim() } });
      } else if (data?.getAllSims) {
        setSimData(mapSimRows(data.getAllSims.data));
      }
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, searchType, data, searchSim, mapSimRows]);
  // Update search results
  useEffect(() => {
    if (searchData?.searchSim && searchTerm.trim().length > 0) {
      setSimData(mapSimRows(searchData.searchSim));
      setTotalCount(searchData.searchSim.length);
    }
  }, [searchData, searchTerm, mapSimRows]);
  // Download Excel export
  const handleDownload = async (): Promise<void> => {
    try {
      const { data } = await downloadInventory();
      if (data?.downloadSimInventory?.url) {
        const link = document.createElement('a');
        link.href = data.downloadSimInventory.url;
        link.setAttribute('download', 'Sim_List.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading inventory:', error);
    }
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (searchTerm.trim().length === 0) {
      refetch({
        pageSize: newRowsPerPage.toString(),
        page: '1'
      });
    }
  };
  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage);
    if (searchTerm.trim().length === 0) {
      refetch({
        pageSize: rowsPerPage.toString(),
        page: (newPage + 1).toString()
      });
    }
  };
  const handleEditSim = (simId: string) => {
    const sim = simData.find((row) => row.sim_id === simId);
    setSelectedSim(sim);
    setEditDialogOpen(true);
  };
  const handleDeleteSim = (simId: string) => {
    const sim = simData.find((row) => row.sim_id === simId);
    setSelectedSim(sim);
    setDeleteDialogOpen(true);
  };
  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    setSelectedSim(null);
    refetch({
      pageSize: rowsPerPage.toString(),
      page: (page + 1).toString()
    });
  };
  const handleAddSimSave = (newSim: any) => {
    setAddSimOpen(false);
    if (newSim && newSim.sim_id) {
      setSimData(prev => [mapSimRows([newSim])[0], ...prev]);
    } else {
      setSearchTerm("");
      setSearchType("imei");
      refetch({
        pageSize: rowsPerPage.toString(),
        page: '1'
      });
    }
  };
  const handleEditSave = () => {
    setEditDialogOpen(false);
    setSelectedSim(null);
    setSearchTerm("");
    setSearchType("imei");
    refetch({
      pageSize: rowsPerPage.toString(),
      page: (page + 1).toString()
    });
  };
  // Table columns
  const tableColumns = [
    "Sr.No",
    "Device Manuf Code",
    "Device Model Code",
    "Device Model",
    "Device IMEI",
    "Device Serial",
    "SIM Provider",
    "ICCID",
    "Activation Type",
    "Activated On",
    "Valid Upto",
    "MSISDN 1",
    "MSISDN 2",
    "WhiteListed IPS",
    "WhiteListed Phone",
    "Whitelist Status",
    "Sim Status",
    "Created By",
    "Created On",
    ...(auth.canCreate("SIM") || auth.canUpdate("SIM") || auth.canDelete("SIM") ? ["Action"] : [])
  ];
  // Table data handling
  const tableData = searchTerm.trim().length > 0
    ? simData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : simData;
  const paginationCount = searchTerm.trim().length > 0 ? simData.length : totalCount;
  const isLoading = allSimsLoading || searchLoading;
  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        SIM Management
      </Typography>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        {/* Sticky header controls */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              <TextField
                select
                size="small"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}
              >
                <MenuItem value="imei">Search by IMEI</MenuItem>
                <MenuItem value="serial">Search by Serial No</MenuItem>
                <MenuItem value="iccid">Search by ICCID</MenuItem>
              </TextField>
              <TextField
                size="small"
                placeholder={
                  searchType === 'imei' ? 'Enter IMEI' :
                    searchType === 'serial' ? 'Enter Serial No' :
                      searchType === 'iccid' ? 'Enter ICCID' : ''
                }
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                inputProps={{ maxLength: 32 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
              />
              <IconButton
                sx={{ ml: 1, color: '#000', flex: '0 0 auto' }}
                onClick={handleDownload}
                disabled={downloadLoading}
              >
                <Tooltip title="Export to Excel" placement="top" arrow>
                  {downloadLoading ? <CircularProgress size={24} /> : <CloudDownloadIcon />}
                </Tooltip>
              </IconButton>
            </Box>
            {(auth.canCreate("SIM") || auth.canUpdate("SIM")) && (
              <Box display="flex" gap={2} flexWrap={{ xs: 'wrap', sm: 'nowrap' }} sx={{ mt: { xs: 1, sm: 0 } }}>
                {(auth.canUpdate("SIM") || auth.canCreate("SIM")) && (
                  <Button
                    startIcon={<SampleFileIcon style={{ width: 24, height: 24 }} />}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: '#000',
                      color: '#fff',
                      borderRadius: '8px',
                      paddingX: 2,
                      paddingY: 1,
                      minWidth: 120,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      '&:hover': {
                        backgroundColor: '#222',
                      },
                      '.MuiButton-startIcon': {
                        color: '#fff',
                      }
                    }}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = '/SIM-Upload-Sample.xlsx';
                      link.download = 'SIM-Upload-Sample.xlsx';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    Sample File
                  </Button>
                )}
                {auth.canCreate("SIM") && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<AddDeviceIcon />}
                      sx={{
                        color: '#fff',
                        backgroundColor: '#E60012',
                        '&:hover': { backgroundColor: '#cc0010' },
                        textTransform: 'none',
                        borderRadius: '8px',
                        paddingX: 2,
                        paddingY: 1,
                        minWidth: 120,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                      onClick={() => setAddSimOpen(true)}
                    >
                      Add New Sim
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<BulkUploadIcon />}
                      sx={{
                        color: '#fff',
                        backgroundColor: '#E60012',
                        '&:hover': { backgroundColor: '#cc0010' },
                        textTransform: 'none',
                        borderRadius: '8px',
                        paddingX: 2,
                        paddingY: 1,
                        minWidth: 120,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                      onClick={() => setBulkUploadOpen(true)}
                    >
                      Bulk Sim Upload
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
        {/* Table area */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 0.5, sm: 2 },
          py: 2,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 600,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 600, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '4px', sm: '6px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : tableData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No SIMs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row, idx) => (
                      <TableRow key={row.sim_id || idx} hover sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                        <TableCell align="center" sx={cellStyle}>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.manuf}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.modelCode || 'N/A'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.model}</TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            maxWidth: 200,
                            whiteSpace: 'nowrap',
                            fontSize: row.imei && row.imei.length > 20 ? '0.85rem' : '0.9rem',
                          }}
                          title={row.imei}
                        >
                          {row.imei}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            minWidth: 200,
                            whiteSpace: 'nowrap',
                          }}
                          title={row.serial}
                        >
                          {row.serial}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.simProvider}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.iccid}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.activationType}</TableCell>
                        <TableCell align="center" sx={{ ...cellStyle }}>
                          {row.activatedOnTsp1 && (
                            <span style={{
                              display: 'inline-block',
                              background: '#eaf4ff',
                              color: '#1976d2',
                              border: '1px solid #90caf9',
                              borderRadius: 12,
                              padding: '4px 16px',
                              marginRight: 12,
                              fontWeight: 500,
                              fontSize: 14
                            }}>{row.activatedOnTsp1}</span>
                          )}
                          {row.activatedOnTsp2 && (
                            <span style={{
                              display: 'inline-block',
                              background: '#fff0f3',
                              color: '#d32f2f',
                              border: '1px solid #f48fb1',
                              borderRadius: 12,
                              padding: '4px 16px',
                              fontWeight: 500,
                              fontSize: 14
                            }}>{row.activatedOnTsp2}</span>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ ...cellStyle }}>
                          {row.validUptoTsp1 && (
                            <span style={{
                              display: 'inline-block',
                              background: '#eaf4ff',
                              color: '#1976d2',
                              border: '1px solid #90caf9',
                              borderRadius: 12,
                              padding: '4px 16px',
                              marginRight: 12,
                              fontWeight: 500,
                              fontSize: 14
                            }}>{row.validUptoTsp1}</span>
                          )}
                          {row.validUptoTsp2 && (
                            <span style={{
                              display: 'inline-block',
                              background: '#fff0f3',
                              color: '#d32f2f',
                              border: '1px solid #f48fb1',
                              borderRadius: 12,
                              padding: '4px 16px',
                              fontWeight: 500,
                              fontSize: 14
                            }}>{row.validUptoTsp2}</span>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.msisdn1}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.msisdn2}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.whiteListedIp || 'N/A'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.whiteListedPhone || 'N/A'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {row.whitelist ? (
                            <Tooltip title={row.whitelist.tooltip} placement="top" arrow>
                              <span style={{
                                display: 'inline-block',
                                background: row.whitelist.bg,
                                color: row.whitelist.color,
                                border: `1px solid ${row.whitelist.border}`,
                                borderRadius: 12,
                                padding: '4px 16px',
                                fontWeight: 500,
                                fontSize: 14
                              }}>{row.whitelist.label}</span>
                            </Tooltip>
                          ) : 'Not whitelisted'}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.simStatus}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.createdBy}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.createdOn}</TableCell>
                        {(auth.canCreate("SIM") || auth.canUpdate("SIM") || auth.canDelete("SIM")) && (
                          <TableCell align="center" sx={cellStyle}>
                            <Box display="flex" justifyContent="center" gap={1}>
                              {auth.canUpdate("SIM") && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSim(row.sim_id)}
                                  sx={{ color: '#000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {auth.canDelete("SIM") && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSim(row.sim_id)}
                                  sx={{ color: '#000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
          mt: 'auto',
          overflowX: 'auto',
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={paginationCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            sx={{
              minWidth: '600px',
              '.MuiTablePagination-toolbar': {
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', sm: 'space-between' },
              },
            }}
          />
        </Box>
      </Card>
      {/* Dialogs */}
      < BulkSimUpload
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onUploaded={() => {
          setBulkUploadOpen(false);
          setSearchTerm("");
          setSearchType("imei");
          refetch({
            pageSize: rowsPerPage.toString(),
            page: '1'
          });
        }}
      />
      < AddNewSim open={addSimOpen} onClose={() => setAddSimOpen(false)} onSave={handleAddSimSave} />
      <EditSimDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSelectedSim(null); }}
        device={selectedSim}
        onSaveSuccess={handleEditSave}
      />
      <DeleteSimDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setSelectedSim(null); }}
        device={selectedSim}
        onDeleted={handleDeleteConfirm}
      />
    </Box>
  );
};
export default SimManagement;